import { useEffect, useState } from 'preact/hooks';
import { StateMessage } from '../../../base/StateMessage/StateMessage';
import { DetailsPage } from '../../../widget/Pages/Pages';
import type { TimetableStopDeparturesDetailsPageOptions } from '../../../widget/pageState';
import { useTimetableDetails } from '../../../../data/useTimetableDetails';
import { useResolvedTimetableLine } from '../../../../data/useResolvedTimetableLine';
import type { TimetableDetailsLine } from '../../../../data/timetableDetailsLine';
import type { TimetableGridRow } from '../Grid/TimetableGrid';
import { TimetableDetailsPage } from './TimetableDetailsPage';
import { useWidgetAnalytics } from '../../../../analytics/useWidgetAnalytics';

interface LineTimetableDetailsPageProps {
  selectedLineId?: string | null;
  initialLine?: TimetableDetailsLine | null;
  onShowStopDeparturesDetails?: (
    options: TimetableStopDeparturesDetailsPageOptions,
  ) => void;
}

/**
 * Widget details-page wrapper for line timetables.
 *
 * Owns local page UI state such as the active tab and stop-display mode,
 * while `useTimetableDetails` stays focused on the selected line's data.
 * Callers can pass a partial details-line model when map/assets links are not
 * available, and the lower-level timetable/map hooks will surface that cleanly.
 */
export function LineTimetableDetailsPage({
  selectedLineId,
  initialLine,
  onShowStopDeparturesDetails,
}: LineTimetableDetailsPageProps) {
  const { track } = useWidgetAnalytics();
  const [activeDetailsTab, setActiveDetailsTab] = useState<'timetable' | 'map'>(
    'timetable',
  );
  const [stopDisplay, setStopDisplay] = useState<'major' | 'all'>('major');
  const [draftStopDisplay, setDraftStopDisplay] = useState<'major' | 'all'>(
    'major',
  );
  const {
    line: selectedLine,
    loading: lineLoading,
    error: lineError,
  } = useResolvedTimetableLine({
    selectedLineId,
    initialLine,
  });
  const {
    timetableLoading,
    timetableError,
    unavailableMessage,
    timetable,
    lineAssets,
    selectedDirection,
    selectedDate,
    updateTimetable,
  } = useTimetableDetails(selectedLine);
  const [draftDirection, setDraftDirection] = useState(selectedDirection);
  const [draftDate, setDraftDate] = useState(selectedDate);

  useEffect(() => {
    setActiveDetailsTab('timetable');
    setStopDisplay('major');
    setDraftStopDisplay('major');
  }, [selectedLineId]);

  useEffect(() => {
    setDraftDirection(selectedDirection);
  }, [selectedDirection]);

  useEffect(() => {
    setDraftDate(selectedDate);
  }, [selectedDate]);

  if (!selectedLine) {
    return (
      <DetailsPage>
        <StateMessage
          state={lineLoading ? 'loading' : 'error'}
          title={
            lineLoading
              ? 'Loading timetable details'
              : 'Timetable details unavailable'
          }
          message={
            lineLoading
              ? 'Fetching the latest service information.'
              : (lineError?.message ??
                "We couldn't load this timetable. Go back to results and try another service.")
          }
        />
      </DetailsPage>
    );
  }

  const handleSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setStopDisplay(draftStopDisplay);
    track({
      name: 'timetable_controls_submitted',
      activity: 'timetables',
      page: 'details',
      payload: {
        lineId: selectedLine.id,
        operatorCode: null,
        direction: draftDirection,
        date: draftDate,
        stopDisplay: draftStopDisplay,
      },
    });
    updateTimetable({
      direction: draftDirection,
      date: draftDate,
    });
  };

  const handleTabChange = (nextTab: 'timetable' | 'map') => {
    if (nextTab === 'map') {
      track({
        name: 'timetable_details_tab_changed',
        activity: 'timetables',
        page: 'details',
        payload: { lineId: selectedLine.id, tabId: nextTab },
      });
    }
    setActiveDetailsTab(nextTab);
  };

  const handleShowStopDeparturesDetails = (waypoint: TimetableGridRow) => {
    const lineId = selectedLine?.id ?? selectedLineId ?? null;

    if (!lineId || !waypoint.stopAtcoCode) {
      return;
    }

    onShowStopDeparturesDetails?.({
      lineId,
      stopAtcoCode: waypoint.stopAtcoCode,
      stopName: waypoint.label,
    });
  };

  return (
    <TimetableDetailsPage
      selectedLine={selectedLine}
      activeDetailsTab={activeDetailsTab}
      onTabChange={handleTabChange}
      timetableLoading={timetableLoading}
      timetableError={timetableError}
      unavailableMessage={unavailableMessage}
      timetable={timetable}
      lineAssets={lineAssets}
      selectedDirection={draftDirection}
      stopDisplay={stopDisplay}
      controlsStopDisplay={draftStopDisplay}
      date={draftDate}
      onDirectionChange={setDraftDirection}
      onStopDisplayChange={setDraftStopDisplay}
      onDateChange={setDraftDate}
      onSubmit={handleSubmit}
      onShowStopDeparturesDetails={handleShowStopDeparturesDetails}
    />
  );
}
